import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { createElement, type ReactNode } from 'react';
import { type EntryLinkDatabaseResourceInput } from '../entry-link/entry-link-field-read-module.ts';
import {
  defineFieldReadModule,
  type CellRenderContext,
  type FieldReadDatabaseSnapshot,
  type FieldReadNestedTarget,
  type FieldReadResource,
  type FieldReadResourceState
} from '../field-read-module.ts';
import {
  EMPTY_ENTRY_DETAILS_CONTENT,
  type EntryDetailsReadContent
} from './entry-details-read-content.ts';
import { EntryDetailsValueView } from './entry-details-value-view.tsx';

export type EntryDetailsReadSnapshot = {
  readonly getTargets: (input: {
    readonly database: FieldReadDatabaseSnapshot;
    readonly entryId: string;
    readonly field: DatabaseField;
  }) => readonly FieldReadNestedTarget[];
};
export type EntryDetailsReadPolicy = {
  readonly getDatabaseSnapshot?: (
    input: EntryLinkDatabaseResourceInput
  ) => EntryDetailsReadSnapshot | undefined;
  readonly getDatabaseResource: (
    input: EntryLinkDatabaseResourceInput
  ) => FieldReadResource<EntryDetailsReadSnapshot>;
};
export type EntryDetailsReadRenderer = (
  content: EntryDetailsReadContent,
  context?: CellRenderContext
) => ReactNode;
/**
 * Keeps relationship metadata compiled once and resolves only the demanded nested values.
 */
export function createEntryDetailsFieldReadModule(
  options?: EntryDetailsReadPolicy & { readonly render?: EntryDetailsReadRenderer }
) {
  return defineFieldReadModule<EntryDetailsReadContent>({
    fieldType: DatabaseFieldType.ENTRY_DETAILS,
    render:
      options?.render ??
      ((content, context) => createElement(EntryDetailsValueView, { content, context })),
    compile(field) {
      const configured = Object.freeze({
        ...field,
        settings: Object.freeze({ ...field.settings })
      });

      return Object.freeze({
        fieldId: field.id,
        resolve: () => EMPTY_ENTRY_DETAILS_CONTENT,
        getResourceRead({ database, address }) {
          if (
            !options ||
            !configured.settings.fieldId ||
            !configured.settings.lookupFieldId
          ) {
            return undefined;
          }

          const relationship = database.getField(configured.settings.fieldId);

          if (
            relationship?.type !== DatabaseFieldType.ENTRY_LINK &&
            relationship?.type !== DatabaseFieldType.BACKLINK
          ) {
            return undefined;
          }

          const input = {
            contentId: relationship.settings.contentId,
            databaseId: relationship.settings.databaseId,
            databaseSpaceKey: relationship.settings.databaseSpace?.key
          };

          if (!input.contentId && !(input.databaseId && input.databaseSpaceKey)) {
            return undefined;
          }

          const cached = options.getDatabaseSnapshot?.(input);
          const source:
            | { readonly snapshot: EntryDetailsReadSnapshot }
            | { readonly resource: FieldReadResource<EntryDetailsReadSnapshot> } = cached
            ? { snapshot: cached }
            : { resource: options.getDatabaseResource(input) };
          let previous: EntryDetailsReadSnapshot | undefined;
          let content = EMPTY_ENTRY_DETAILS_CONTENT;

          return Object.freeze({
            resources: Object.freeze('resource' in source ? [source.resource] : []),
            resolve(
              read: <Value>(
                resource: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ) {
              const state: FieldReadResourceState<EntryDetailsReadSnapshot> =
                'resource' in source
                  ? read(source.resource)
                  : { status: 'ready', value: source.snapshot };

              if (state.status !== 'ready') {
                return EMPTY_ENTRY_DETAILS_CONTENT;
              }

              if (previous !== state.value) {
                const targets = state.value.getTargets({
                  database,
                  entryId: address.entryId,
                  field: configured
                });

                previous = state.value;
                content = targets.length
                  ? Object.freeze({ kind: 'entry-details', targets })
                  : EMPTY_ENTRY_DETAILS_CONTENT;
              }

              return content;
            }
          });
        }
      });
    }
  });
}
